/**
 * The colour a category can carry — shown as a small swatch wherever
 * categories are listed (the config page, review reports, the role matrix).
 *
 * This is a categorical tag colour an admin picks per category purely to
 * tell them apart at a glance, not a semantic status colour, so it does not
 * need to track the brand theme the way `--success`/`--warning` do (see
 * `.claude/rules/design-system.md` on categorical vs semantic colour) — it's
 * stored and painted as a plain hex value, picked with a native colour input
 * (a real colour wheel in most browsers), not a fixed list of named tokens.
 *
 * A handful of categories still carry one of the five tokens this used
 * before the palette widened (2026-10-15) — `LEGACY_TOKENS` keeps those
 * resolving via the CSS variable so nothing already saved goes blank. New
 * values are always hex; nothing writes a legacy token anymore.
 */
const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

const LEGACY_TOKENS = [
  { token: "chart-1", label: "Blue (legacy)" },
  { token: "chart-2", label: "Steel (legacy)" },
  { token: "chart-3", label: "Indigo (legacy)" },
  { token: "chart-4", label: "Violet (legacy)" },
  { token: "chart-5", label: "Sky (legacy)" },
] as const;

/** Starting point the colour picker opens on for a category with none yet. */
export const DEFAULT_CATEGORY_COLOR = "#2563eb";

export function isHexCategoryColor(value: string): boolean {
  return HEX_COLOR_RE.test(value);
}

/** Whether a value can be stored — a hex colour, or (still) one of the legacy tokens. */
export function isValidCategoryColor(value: string): boolean {
  return isHexCategoryColor(value) || LEGACY_TOKENS.some((t) => t.token === value);
}

/**
 * The CSS value to paint a stored colour with, or null for "no colour set".
 *
 * An unrecognised legacy token — a row written before a palette change —
 * returns null rather than a broken `var(--…)`, so a dropped colour degrades
 * to no colour instead of an invisible swatch.
 */
export function categoryColorVar(value: string | null | undefined): string | null {
  if (!value) return null;
  if (isHexCategoryColor(value)) return value;
  const legacy = LEGACY_TOKENS.find((t) => t.token === value);
  return legacy ? `var(--${legacy.token})` : null;
}

/** Display text for a stored colour, or null when it is unset or unknown. */
export function categoryColorLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  if (isHexCategoryColor(value)) return value.toUpperCase();
  return LEGACY_TOKENS.find((t) => t.token === value)?.label ?? null;
}
